var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { Loader } from '@ui';
import { Toggle } from '@ui/molecules/inputs/Toggle';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
import { SelectInput } from '@ui/molecules/inputs/form/SelectInput';
import { Tag } from '@ui/atoms/Tag';
import { listCheckConfigs, createCheckConfig, updateCheckConfig, deleteCheckConfig, searchBuildings, searchAccounts } from '../services/TkbService';
import AppliesTo from './AppliesTo';
const CHECK_CONFIGS_KEY = ['tkb', 'check_configs'];
const SCOPED_LEVELS = ['account', 'account_tag', 'building', 'building_tag'];
// IP-14304: -1 is safe as a "nothing selected" sentinel — issue_type=0 is the valid
// 'unanswered_questions' enum value, so 0 cannot be used as a placeholder.
const NO_ISSUE_TYPE = -1;
const EMPTY_FORM = {
    isOpen: false,
    scopeLevel: 'building',
    issueType: '',
    enabled: true,
    buildingId: null,
    buildingLabel: '',
    buildingQuery: '',
    customerAccountId: null,
    accountLabel: '',
    accountQuery: '',
    buildingTag: '',
    accountTag: ''
};
/**
 * Checks tab (task-11 §Step 2): matrix of ISSUE_TYPES × scope.
 * - Tenant defaults are read-only here (they reflect the flat
 *   ai_inspection_manager_* flags resolved by the engine).
 * - Scoped override rows (account / building / building_tag) are CRUD'd against
 *   ia_check_config and rendered with the inherited-vs-overridden badge pattern.
 *
 * IP-14304: scope_key input replaced with typeahead/SelectInput pickers per
 * axis, matching the CheckOverrides.tsx pattern. A typo'd building id can no
 * longer create a config that silently never matches.
 */
export default function ChecksTab({ issueTypes, buildingTags = [], accountTags = [], t }) {
    var _a, _b, _c;
    const qc = useQueryClient();
    const [form, setForm] = useState(EMPTY_FORM);
    const [deleteId, setDeleteId] = useState(null);
    const [formError, setFormError] = useState('');
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [buildingLoading, setBuildingLoading] = useState(false);
    // Guards against stale async results: an older in-flight searchBuildings
    // response must not overwrite a newer query's results (which would show the
    // wrong building list and risk saving the wrong scope_key).
    const latestBuildingSearchId = useRef(0);
    const [accountSearchOptions, setAccountSearchOptions] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);
    // Same stale-result guard for account search.
    const latestAccountSearchId = useRef(0);
    const { data, isLoading, isError, error } = useQuery({
        queryKey: CHECK_CONFIGS_KEY,
        queryFn: listCheckConfigs
    });
    const invalidate = () => {
        qc.invalidateQueries({ queryKey: CHECK_CONFIGS_KEY });
    };
    const createMutation = useMutation({
        mutationFn: createCheckConfig,
        onSuccess: () => {
            invalidate();
            setForm(EMPTY_FORM);
        },
        onError: (err) => setFormError(err.message)
    });
    const toggleMutation = useMutation({
        mutationFn: ({ id, enabled }) => updateCheckConfig(id, { enabled }),
        onSuccess: invalidate
    });
    const deleteMutation = useMutation({
        mutationFn: (id) => deleteCheckConfig(id),
        onSuccess: () => {
            invalidate();
            setDeleteId(null);
        }
    });
    const issueTypeName = (value) => { var _a, _b; return (_b = (_a = issueTypes.find((it) => it.value === value)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : String(value); };
    // ── SelectInput option lists built from bootstrapped props ─────────────────
    const issueTypeOptions = useMemo(() => issueTypes.map((it) => ({ value: it.value, label: it.name })), [issueTypes]);
    const buildingTagOptions = useMemo(() => buildingTags.map((tag) => ({ value: tag, label: tag })), [buildingTags]);
    const accountTagOptions = useMemo(() => accountTags.map((tag) => ({ value: tag, label: tag })), [accountTags]);
    const buildingSelectOptions = useMemo(() => buildingOptions.map((b) => ({ value: b.value, label: b.label })), [buildingOptions]);
    const accountSelectOptions = useMemo(() => accountSearchOptions.map((a) => ({ value: a.value, label: a.label })), [accountSearchOptions]);
    // ── Building typeahead (mirrors CheckOverrides.tsx) ────────────────────────
    const handleBuildingSearch = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        const searchId = ++latestBuildingSearchId.current;
        setForm((prev) => (Object.assign(Object.assign({}, prev), { buildingQuery: query })));
        if (query.length < 2) {
            setBuildingOptions([]);
            setBuildingLoading(false);
            return;
        }
        setBuildingLoading(true);
        try {
            const results = yield searchBuildings(query);
            if (searchId !== latestBuildingSearchId.current)
                return;
            setBuildingOptions(results);
        }
        catch (_a) {
            if (searchId !== latestBuildingSearchId.current)
                return;
            setBuildingOptions([]);
        }
        finally {
            if (searchId === latestBuildingSearchId.current) {
                setBuildingLoading(false);
            }
        }
    }), []);
    // ── Account typeahead (mirrors building typeahead above) ──────────────────
    const handleAccountSearch = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        const searchId = ++latestAccountSearchId.current;
        setForm((prev) => (Object.assign(Object.assign({}, prev), { accountQuery: query })));
        if (query.length < 2) {
            setAccountSearchOptions([]);
            setAccountLoading(false);
            return;
        }
        setAccountLoading(true);
        try {
            const results = yield searchAccounts(query);
            if (searchId !== latestAccountSearchId.current)
                return;
            setAccountSearchOptions(results);
        }
        catch (_a) {
            if (searchId !== latestAccountSearchId.current)
                return;
            setAccountSearchOptions([]);
        }
        finally {
            if (searchId === latestAccountSearchId.current) {
                setAccountLoading(false);
            }
        }
    }), []);
    // ── Scope key derivation ───────────────────────────────────────────────────
    /** Returns the validated scope_key string for the current axis, or null. */
    const scopeKeyFromForm = () => {
        if (form.scopeLevel === 'building') {
            return form.buildingId != null ? String(form.buildingId) : null;
        }
        if (form.scopeLevel === 'account') {
            return form.customerAccountId != null ? String(form.customerAccountId) : null;
        }
        if (form.scopeLevel === 'building_tag') {
            return form.buildingTag.trim() || null;
        }
        if (form.scopeLevel === 'account_tag') {
            return form.accountTag.trim() || null;
        }
        return null;
    };
    // ── Submit ─────────────────────────────────────────────────────────────────
    const handleSubmit = () => {
        if (form.issueType === '') {
            setFormError(t('checks.form.validation_required'));
            return;
        }
        const scopeKey = scopeKeyFromForm();
        if (scopeKey === null) {
            setFormError(t('checks.form.scope_key_required'));
            return;
        }
        createMutation.mutate({
            scope_level: form.scopeLevel,
            scope_key: scopeKey,
            issue_type: form.issueType,
            enabled: form.enabled
        });
    };
    const openForm = () => {
        setBuildingOptions([]);
        setAccountSearchOptions([]);
        setFormError('');
        setForm(Object.assign(Object.assign({}, EMPTY_FORM), { isOpen: true }));
    };
    // ── When scope level changes, clear all axis state ─────────────────────────
    const handleScopeLevelChange = (lvl) => {
        setForm((prev) => (Object.assign(Object.assign({}, prev), { scopeLevel: lvl, buildingId: null, buildingLabel: '', buildingQuery: '', customerAccountId: null, accountLabel: '', accountQuery: '', buildingTag: '', accountTag: '' })));
        setBuildingOptions([]);
        setAccountSearchOptions([]);
    };
    // ── Loading / error states ─────────────────────────────────────────────────
    if (isLoading) {
        return (React.createElement("div", { className: "loading-center" },
            React.createElement(Loader, { size: "64" })));
    }
    if (isError) {
        return (React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)' } }, (error === null || error === void 0 ? void 0 : error.message) || t('checks.load_failure')));
    }
    const overrides = (_a = data === null || data === void 0 ? void 0 : data.check_configs) !== null && _a !== void 0 ? _a : [];
    return (React.createElement("div", { "data-testid": "checks-tab" },
        React.createElement(Text, { variant: "body", as: "p" }, t('checks.intro')),
        React.createElement("article", { className: "qmb-filters" },
            React.createElement("button", { type: "button", className: "qmb-control mt-1", onClick: openForm },
                React.createElement("i", { className: "fa-light fa-circle-plus" }),
                t('checks.add_button'))),
        React.createElement("table", { className: "table table--listing" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, t('checks.headers.check')),
                    React.createElement("th", null, t('checks.headers.scope')),
                    React.createElement("th", null, t('checks.headers.enabled')),
                    React.createElement("th", { style: { width: '96px' } }, t('checks.headers.actions')))),
            React.createElement("tbody", null,
                overrides.length === 0 && (React.createElement("tr", null,
                    React.createElement("td", { colSpan: 4, className: "no-data-message", style: { textAlign: 'center', padding: '16px' } }, t('checks.empty')))),
                overrides.map((cc) => (React.createElement("tr", { key: cc.id, "data-testid": `check-config-${cc.id}` },
                    React.createElement("td", null, issueTypeName(cc.issue_type)),
                    React.createElement("td", null,
                        React.createElement(AppliesTo, { scopeLevel: cc.scope_level, targetLabel: cc.scope_label, t: t })),
                    React.createElement("td", null,
                        React.createElement(Toggle, { checked: cc.enabled, onChange: () => toggleMutation.mutate({ id: cc.id, enabled: !cc.enabled }), disabled: toggleMutation.isLoading, "aria-label": t('checks.toggle_aria', { check: issueTypeName(cc.issue_type) }) })),
                    React.createElement("td", null,
                        React.createElement("button", { type: "button", title: t('checks.actions.delete'), "aria-label": t('checks.actions.delete_aria', { check: issueTypeName(cc.issue_type) }), className: "qmb-control--icon", onClick: () => setDeleteId(cc.id) },
                            React.createElement("i", { className: "fa-light fa-trash-can", "aria-hidden": "true" })))))))),
        React.createElement(Modal, { isOpen: form.isOpen, onClose: () => setForm(EMPTY_FORM), ariaLabel: t('checks.form.add_title') },
            React.createElement(ModalHeader, { title: t('checks.form.add_title') }),
            React.createElement(ModalBody, null,
                formError && (React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)', marginBottom: '12px' } }, formError)),
                React.createElement("div", { style: { marginBottom: '16px' } },
                    React.createElement(SelectInput, { id: "check-issue-type", label: t('checks.form.check'), options: [{ value: NO_ISSUE_TYPE, label: t('checks.form.select_check') }, ...issueTypeOptions], value: form.issueType === '' ? NO_ISSUE_TYPE : form.issueType, onChange: (val) => setForm((prev) => (Object.assign(Object.assign({}, prev), { issueType: val === NO_ISSUE_TYPE ? '' : val }))), showSearch: true })),
                React.createElement("div", { style: { marginBottom: '16px' } },
                    React.createElement(SelectInput, { id: "check-scope-level", label: t('checks.form.scope_level'), options: SCOPED_LEVELS.map((lvl) => ({
                            value: lvl,
                            label: t(`checks.form.scope_level_options.${lvl}`)
                        })), value: form.scopeLevel, onChange: handleScopeLevelChange })),
                form.scopeLevel === 'building' && (React.createElement("div", { style: { marginBottom: '16px' } },
                    React.createElement("label", { className: "qmb-label", htmlFor: "check-building-search" }, t('checks.form.scope_key')),
                    React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' } },
                        React.createElement("input", { id: "check-building-search", type: "text", className: "qmb-input--text", "data-testid": "check-building-search", placeholder: t('checks.form.building_search_placeholder'), value: form.buildingQuery, onChange: (e) => {
                                void handleBuildingSearch(e.target.value);
                            }, autoComplete: "off", style: { flex: 1 } }),
                        buildingLoading && React.createElement(Loader, { size: "16" })),
                    buildingSelectOptions.length > 0 && (React.createElement("div", { style: { marginTop: '8px' } },
                        React.createElement(SelectInput, { id: "check-building-select", label: "", options: buildingSelectOptions, value: (_b = form.buildingId) !== null && _b !== void 0 ? _b : undefined, onChange: (val) => {
                                const opt = buildingOptions.find((b) => b.value === val);
                                setForm((prev) => {
                                    var _a;
                                    return (Object.assign(Object.assign({}, prev), { buildingId: val, buildingLabel: (_a = opt === null || opt === void 0 ? void 0 : opt.label) !== null && _a !== void 0 ? _a : '', buildingQuery: '' }));
                                });
                                setBuildingOptions([]);
                            } }))),
                    form.buildingId != null && (React.createElement("div", { className: "tkb-scope-picker__selected", style: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' } },
                        React.createElement(Tag, { label: form.buildingLabel || String(form.buildingId), variant: "green", readOnly: true }),
                        React.createElement("button", { type: "button", className: "qmb-control--icon", "aria-label": t('checks.form.clear_building'), onClick: () => setForm((prev) => (Object.assign(Object.assign({}, prev), { buildingId: null, buildingLabel: '' }))) },
                            React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" })))))),
                form.scopeLevel === 'account' && (React.createElement("div", { style: { marginBottom: '16px' } },
                    React.createElement("label", { className: "qmb-label", htmlFor: "check-account-search" }, t('checks.form.scope_key')),
                    React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' } },
                        React.createElement("input", { id: "check-account-search", type: "text", className: "qmb-input--text", "data-testid": "check-account-search", placeholder: t('checks.form.account_search_placeholder'), value: form.accountQuery, onChange: (e) => {
                                void handleAccountSearch(e.target.value);
                            }, autoComplete: "off", style: { flex: 1 } }),
                        accountLoading && React.createElement(Loader, { size: "16" })),
                    accountSelectOptions.length > 0 && (React.createElement("div", { style: { marginTop: '8px' } },
                        React.createElement(SelectInput, { id: "check-account-select", label: "", options: accountSelectOptions, value: (_c = form.customerAccountId) !== null && _c !== void 0 ? _c : undefined, onChange: (val) => {
                                const opt = accountSearchOptions.find((a) => a.value === val);
                                setForm((prev) => {
                                    var _a;
                                    return (Object.assign(Object.assign({}, prev), { customerAccountId: val, accountLabel: (_a = opt === null || opt === void 0 ? void 0 : opt.label) !== null && _a !== void 0 ? _a : '', accountQuery: '' }));
                                });
                                setAccountSearchOptions([]);
                            } }))),
                    form.customerAccountId != null && (React.createElement("div", { className: "tkb-scope-picker__selected", style: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' } },
                        React.createElement(Tag, { label: form.accountLabel || String(form.customerAccountId), variant: "green", readOnly: true }),
                        React.createElement("button", { type: "button", className: "qmb-control--icon", "aria-label": t('checks.form.clear_account'), onClick: () => setForm((prev) => (Object.assign(Object.assign({}, prev), { customerAccountId: null, accountLabel: '' }))) },
                            React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" })))))),
                form.scopeLevel === 'building_tag' && (React.createElement("div", { style: { marginBottom: '16px' } },
                    React.createElement(SelectInput, { id: "check-building-tag", label: t('checks.form.scope_key'), options: [{ value: '', label: t('checks.form.select_building_tag') }, ...buildingTagOptions], value: form.buildingTag, onChange: (val) => setForm((prev) => (Object.assign(Object.assign({}, prev), { buildingTag: val }))), showSearch: true, searchPlaceholder: t('checks.form.tag_search_placeholder') }))),
                form.scopeLevel === 'account_tag' && (React.createElement("div", { style: { marginBottom: '16px' } },
                    React.createElement(SelectInput, { id: "check-account-tag", label: t('checks.form.scope_key'), options: [{ value: '', label: t('checks.form.select_account_tag') }, ...accountTagOptions], value: form.accountTag, onChange: (val) => setForm((prev) => (Object.assign(Object.assign({}, prev), { accountTag: val }))), showSearch: true, searchPlaceholder: t('checks.form.account_tag_search_placeholder') }))),
                React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                    React.createElement(Toggle, { id: "check-enabled", checked: form.enabled, onChange: (e) => setForm(Object.assign(Object.assign({}, form), { enabled: e.target.checked })), "aria-label": t('checks.form.enabled') }),
                    React.createElement("label", { htmlFor: "check-enabled" }, t('checks.form.enabled')))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: () => setForm(EMPTY_FORM), style: { marginRight: '8px' } }, t('checks.form.cancel')),
                React.createElement(Button, { variant: "primary", disabled: createMutation.isLoading, onClick: handleSubmit }, createMutation.isLoading ? t('checks.form.saving') : t('checks.form.save')))),
        React.createElement(Modal, { isOpen: deleteId !== null, onClose: () => setDeleteId(null), ariaLabel: t('checks.delete_modal.title') },
            React.createElement(ModalHeader, { title: t('checks.delete_modal.title') }),
            React.createElement(ModalBody, null,
                React.createElement(Text, null, t('checks.delete_modal.body'))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: () => setDeleteId(null), style: { marginRight: '8px' } }, t('checks.delete_modal.cancel')),
                React.createElement(Button, { variant: "danger", disabled: deleteMutation.isLoading, onClick: () => {
                        if (deleteId !== null)
                            deleteMutation.mutate(deleteId);
                    } }, t('checks.delete_modal.confirm'))))));
}
